import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { forkJoin } from 'rxjs';
import { Router } from '@angular/router';

import {
  ACH_METHOD_NOT_ALLOWED_WITH_PUN,
  ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
  ARRIVAL_DATE_MISSING,
  ARRIVAL_DATE_TOO_EARLY,
  BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY,
  EXPORT_DATE_LATER_THAN_ARRIVAL_DATE,
  EXPORT_DATE_MISSING,
  FLIGHT_NUMBER_MISSING,
  FOREIGN_PORT_MISSING,
  FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER,
  FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL,
  FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
  INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG,
  INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
  LOCATION_MISSING,
  PAYER_UNIT_NUMBER_MALFORMED,
  PAYER_UNIT_NUMBER_MISSING,
  PORT_OF_UNLADING_MISSING,
  STATE_OF_DESTINATION_MISSING,
  TRIP_MISSING,
  VESSEL_NAME_MISSING,
  VESSEL_NAME_TOO_LONG,
} from '@constants/entity-validation-messages';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { ForeignPortModel, LocationOfGoodsModel, PortModel, StateModel, ValidationMessageModel } from '@data-layer/models/common';
import { CodeRepository } from '@data-layer/repositories';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { take } from 'rxjs/operators';
import { UtilsHelper } from '@helpers/utils-helper';

@Component({
  selector: 'es-details-shipment-card',
  templateUrl: './es-details-shipment-card.component.html',
  styleUrls: ['./es-details-shipment-card.component.scss'],
})
export class EsDetailsShipmentCardComponent implements OnInit, OnChanges {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public canEdit = false;
  @Input() public allValidationMessages: ValidationMessageModel[] = [];
  @Output() public edit = new EventEmitter();
  public info = {
    title: 'INFO.SHIP.TITLE',
    imgSrc: 'vessel',
  };
  public exportDate = null;
  public foreignPort: ForeignPortModel = null;
  public location: LocationOfGoodsModel = null;
  public stateOfDestination: StateModel = null;
  public isEmpty = false;
  public currentCardValidationMessages: ValidationMessageModel[] = [];
  public enumShipmentType: typeof enumEntrySummaryType = enumEntrySummaryType;
  public enumEntrySummarySubmissionType: typeof enumEntrySummarySubmissionType = enumEntrySummarySubmissionType;
  public enumEntrySummaryDutiesPaymentType: typeof enumEntrySummaryDutiesPaymentType = enumEntrySummaryDutiesPaymentType;

  private readonly missingFields = [
    ARRIVAL_DATE_MISSING,
    LOCATION_MISSING,
    PORT_OF_UNLADING_MISSING,
    TRIP_MISSING,
    FLIGHT_NUMBER_MISSING,
    VESSEL_NAME_MISSING,
    VESSEL_NAME_TOO_LONG,
    PAYER_UNIT_NUMBER_MISSING,
    PAYER_UNIT_NUMBER_MALFORMED,
    STATE_OF_DESTINATION_MISSING,
    FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
    INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
    FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL,
    INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG,
    BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY,
    FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER,
    ARRIVAL_DATE_TOO_EARLY,
    ACH_METHOD_NOT_ALLOWED_WITH_PUN,
    ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
  ];
  private missingFieldsOnShipment: string[] = [];
  private ports: PortModel[] = [];

  constructor(private router: Router, private codeRepository: CodeRepository) {}

  public ngOnInit(): void {
    this.updateMissingFieldsOnShipment();
    this.getPorts();
    this.checkIsMissing();
  }

  public ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.entrySummary) {
      this.updateMissingFieldsOnShipment();
      this.getPorts();
      this.checkIsMissing();

      let imgSrc = null;
      switch (this.entrySummary?.transportationMode) {
        case enumTransportationMode.AIR:
          imgSrc = 'ic_plain';
          break;
        case enumTransportationMode.OCEAN:
          imgSrc = 'vessel';
          break;
        case enumTransportationMode.RAIL:
          imgSrc = 'rail';
          break;
        case enumTransportationMode.TRUCK:
          imgSrc = 'icon_truck';
          break;
        default:
          break;
      }

      this.info.imgSrc = imgSrc;
    }

    if (simpleChanges.allValidationMessages) {
      this.checkIsMissing();
    }
  }

  public get isShowDutiesPaymentType() {
    return this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED;
  }

  public get portOfEntry() {
    return this.ports.find((port) => port.code === this.entrySummary.portOfEntryCode);
  }

  public get portOfUnlading() {
    return this.ports.find((port) => port.code === this.entrySummary.portOfUnladingCode);
  }

  public get isAir() {
    return this.entrySummary.transportationMode === enumTransportationMode.AIR;
  }

  public get isRail() {
    return this.entrySummary.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.entrySummary.transportationMode === enumTransportationMode.TRUCK;
  }

  public goToEdit() {
    this.edit.emit();
  }

  public get tripLabel(): string {
    switch (this.entrySummary?.transportationMode) {
      case enumTransportationMode.AIR:
        return 'FORM_LABEL.FLIGHT_NUMBER';
      case enumTransportationMode.OCEAN:
        return 'LABELS.VOYAGE_NUMBER';
      case enumTransportationMode.RAIL:
        return 'FORM_LABEL.RAIL_TRIP_NUMBER';
      default:
        break;
    }
  }

  private updateMissingFieldsOnShipment(): void {
    this.missingFieldsOnShipment =
      this.entrySummary?.transportationMode === enumTransportationMode.TRUCK
        ? [EXPORT_DATE_MISSING, EXPORT_DATE_LATER_THAN_ARRIVAL_DATE]
        : [EXPORT_DATE_MISSING, FOREIGN_PORT_MISSING, EXPORT_DATE_LATER_THAN_ARRIVAL_DATE];
  }

  private getPorts() {
    if (!this.entrySummary) {
      return;
    }

    const { hasSingleExportData, shipments, portOfEntryCode, portOfUnladingCode, locationCode, stateOfDestination } = this.entrySummary;

    if (!hasSingleExportData && shipments && shipments[0]) {
      const { exportDate, foreignPortOfLadingId } = shipments[0];
      this.exportDate = exportDate;

      if (foreignPortOfLadingId && (!this.foreignPort || foreignPortOfLadingId !== this.foreignPort.id)) {
        this.codeRepository
          .getForeignPort(foreignPortOfLadingId)
          .pipe(take(1))
          .subscribe((port) => {
            this.foreignPort = port;
          });
      }
    } else {
      this.foreignPort = null;
      this.exportDate = null;
    }

    const filterCode = (code: string) => code && !this.ports.find((exPort) => exPort.code === code);
    const portsForSearch = UtilsHelper.uniq<string>([portOfEntryCode, portOfUnladingCode]).filter(filterCode);

    if (portsForSearch.length) {
      forkJoin(portsForSearch.map((code) => this.codeRepository.getDomesticPort(code)))
        .pipe(take(1))
        .subscribe((resp) => {
          this.ports = resp;
        });
    }

    if (locationCode && (!this.location || this.location.code !== locationCode)) {
      this.codeRepository
        .getLocation(locationCode)
        .pipe(take(1))
        .subscribe((location) => {
          this.location = location;
        });
    } else if (locationCode == null) {
      this.location = null;
    }

    if (stateOfDestination && (!this.stateOfDestination || this.stateOfDestination.code !== stateOfDestination)) {
      this.codeRepository
        .getState(stateOfDestination)
        .pipe(take(1))
        .subscribe((state) => {
          this.stateOfDestination = state;
        });
    }
  }

  private checkIsMissing() {
    const firstShipment = this.entrySummary.shipments[0];
    this.currentCardValidationMessages = [];

    for (const mess of this.allValidationMessages) {
      if (
        this.missingFields.some((error) => mess.text.endsWith(error)) ||
        (!this.entrySummary.hasSingleExportData && this.missingFieldsOnShipment.some((error) => mess.text.endsWith(error)))
      ) {
        this.currentCardValidationMessages.push(mess);
      }
    }

    const requiredFields = ['arrivalDate', 'locationCode', 'portOfEntryCode', 'portOfUnladingCode', 'trip', 'vesselName'];
    const requiredFieldsOnShipment = this.entrySummary.hasSingleExportData ? [] : ['foreignPortOfLadingId', 'exportDate'];
    this.isEmpty = true;
    for (const field of requiredFields) {
      if (![null, undefined, ''].includes(this.entrySummary[field])) {
        this.isEmpty = false;
      }
    }

    if (firstShipment) {
      for (const field of requiredFieldsOnShipment) {
        if (![null, undefined, ''].includes(firstShipment[field])) {
          this.isEmpty = false;
        }
      }
    }
  }
}
